import { useCallback, useMemo } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { format } from 'date-fns';
import { includes } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelists } from 'constants/codelists';
import MODALS from 'constants/modals';
import { KCL_APPLY_POLICY_SUBSELLER_FILTER } from 'features/common/acl/constants';
import { removeFilterCriteriaByKey, saveFocusField, search } from 'features/views/policyOverview/actions';
import { checkboxFields } from 'features/views/policyOverview/constants';
import { FIELDS, POLICY_OVERVIEW_FILTER_FORM_NAME } from 'features/views/policyOverview/form';
import { getSearchedCriterias } from 'features/views/policyOverview/selectors';
import { getCodelistByName, getCodelistPropById } from 'utils';
import { joinString } from 'utils/string';

export const FilterCriteriaChips = () => {
	const dispatch = useDispatch();
	const next = useControlNext(POLICY_OVERVIEW_FILTER_FORM_NAME);
	const advancedSearchModal = useModal(MODALS.policyOverview_advanced_search);

	const searchedCriterias = useSelector(getSearchedCriterias);

	const hasPermissionToRemoveSellerLogin = useSelector((state) =>
		hasPermissionByIds(state)(KCL_APPLY_POLICY_SUBSELLER_FILTER),
	);

	const policyStatusesCodelist = useSelector(getCodelistByName(codelists.policyStatuses));
	const policyPaymentStatusesCodelist = useSelector(getCodelistByName(codelists.policyPaymentStatuses));
	const productDomainsCodelist = useSelector(getCodelistByName(codelists.productDomains));
	const productsCodelist = useSelector(getCodelistByName(codelists.products));
	const semaphoreCodelist = useSelector(getCodelistByName(codelists.semaphoreSegmentation));
	const productsFullName = useMemo(
		() =>
			productsCodelist.map((item) => ({
				...item,
				fullName: joinString(' - ', [item?.shortDescription, item?.jokName]),
			})),
		[productsCodelist],
	);

	const prepareFormData = (formData) => {
		const dataAsObjects = Object.keys(formData).map((key) => ({ key, value: formData[key] }));
		const dataAsObjectsFiltered = dataAsObjects.filter(
			(item) => !(includes(item.key, checkboxFields) && item.value === false),
		);

		const valueParser = ({ key, value }) => {
			const datePickerFields = [
				FIELDS.policyValueDateFrom,
				FIELDS.policyValueDateTo,
				FIELDS.policyOpenDateFrom,
				FIELDS.policyOpenDateTo,
				FIELDS.policyFinalDateFrom,
				FIELDS.policyFinalDateTo,
			];

			if (includes(key, datePickerFields)) {
				return format(new Date(value), 'dd/MM/yyyy');
			} else if (includes(key, checkboxFields)) {
				return null;
			} else {
				switch (key) {
					case FIELDS.policyStatus:
						return getCodelistPropById(value, policyStatusesCodelist);
					case FIELDS.productDomain:
						return getCodelistPropById(value, productDomainsCodelist, 'shortDescription');
					case FIELDS.product:
						return getCodelistPropById(value, productsFullName, 'fullName');
					case FIELDS.policyPaymentStatus:
						return getCodelistPropById(value, policyPaymentStatusesCodelist, 'shortDescription');
					case FIELDS.semaphore:
						return getCodelistPropById(value, semaphoreCodelist);
					default:
						return value;
				}
			}
		};

		const parsedData = dataAsObjectsFiltered.map(({ key, value }) => {
			const localizedKey = getLabelMessage(`policyOverview:${key}`);

			return {
				originalKey: key,
				key: localizedKey,
				value: valueParser({ key, value }),
			};
		});

		return parsedData;
	};

	const arrayFormData = prepareFormData(searchedCriterias);

	const handleDelete = useCallback(
		(key) => () => {
			if (key === FIELDS.productDomain) {
				dispatch(removeFilterCriteriaByKey(key));
				dispatch(removeFilterCriteriaByKey(FIELDS.product));
				next(setValue({ fieldName: FIELDS[key], value: null }));
				next(setValue({ fieldName: FIELDS.product, value: null }));
			} else {
				dispatch(removeFilterCriteriaByKey(key));
				next(setValue({ fieldName: FIELDS[key], value: null }));
			}
			dispatch(search());
		},
		[dispatch, next],
	);

	const handleClick = useCallback(
		(key) => () => {
			dispatch(saveFocusField(key));
			advancedSearchModal.show();
		},
		[advancedSearchModal, dispatch],
	);

	const generateOnDelete = useCallback(
		(key) => {
			if (key !== FIELDS.sellerLogin) {
				return handleDelete(key);
			} else {
				return hasPermissionToRemoveSellerLogin && handleDelete(key);
			}
		},
		[handleDelete, hasPermissionToRemoveSellerLogin],
	);

	return (
		<Stack
			direction="row"
			spacing={1}
			useFlexGap
			sx={{
				flexWrap: 'wrap',
			}}
		>
			{arrayFormData.map((item) => (
				<Chip
					key={item.originalKey}
					label={joinString(' - ', [item.key, item.value])}
					onDelete={generateOnDelete(item.originalKey)}
					onClick={handleClick(item.originalKey)}
					size="small"
				/>
			))}
		</Stack>
	);
};

export default FilterCriteriaChips;
